import { useState, useEffect } from 'react';
import { Users, UserCircle, Activity, TrendingUp, Award, Building2, Loader2 } from 'lucide-react';
import { fetchAdminDashboardStats } from '@/lib/api';
import { useCpdEnabled } from '@/contexts/TenantContext';

export function KeyMetrics() {
  const cpdEnabled = useCpdEnabled();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    async function loadStats() {
      setLoading(true);
      const data = await fetchAdminDashboardStats(undefined, { includeCpd: cpdEnabled });
      setStats(data);
      setLoading(false);
    }
    loadStats();
  }, [cpdEnabled]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin h-8 w-8 text-primary" />
      </div>
    );
  }

  if (!stats) return null;

  const metrics = [
    {
      label: 'Total Users',
      value: String(stats.totalUsers || 0),
      change: `+${stats.activeUsers || 0}`,
      changePercent: '+9.2%',
      trend: 'up',
      icon: Users,
      color: 'blue',
      description: 'Active registered users'
    },
    {
      label: 'Total Agents',
      value: String(stats.totalAgents || 0),
      change: '+3',
      changePercent: '+8.6%',
      trend: 'up',
      icon: UserCircle,
      color: 'green',
      description: `${stats.patientAgents || 0} Patient, ${stats.customerAgents || 0} Customer, ${stats.examinerAgents || 0} Examiner`
    },
    {
      label: 'Simulations Completed',
      value: String(stats.totalSimulations || 0),
      change: '+247',
      changePercent: '+15.4%',
      trend: 'up',
      icon: Activity,
      color: 'purple',
      description: 'Platform-wide'
    },
    {
      label: 'Avg Platform Score',
      value: stats.avgPlatformScore == null ? '—' : `${stats.avgPlatformScore}/100`,
      change: '+3',
      changePercent: '+3.8%',
      trend: 'up',
      icon: TrendingUp,
      color: 'orange',
      description: 'Across all users'
    },
    ...(cpdEnabled ? [{
      label: 'CPD Points Issued',
      value: String(stats.cpdPointsIssued || 0),
      change: 'N/A',
      changePercent: '+0%',
      trend: 'up',
      icon: Award,
      color: 'yellow',
      description: 'Total distributed'
    }] : []),
    {
      label: 'Companies',
      value: String(stats.totalCompanies || 0),
      change: 'N/A',
      changePercent: '+0%',
      trend: 'up',
      icon: Building2,
      color: 'indigo',
      description: 'Active organizations'
    },
  ];

  const colorMap = {
    blue: 'from-blue-50 to-blue-100 text-blue-600',
    green: 'from-green-50 to-green-100 text-green-600',
    purple: 'from-purple-50 to-purple-100 text-purple-600',
    orange: 'from-orange-50 to-orange-100 text-orange-600',
    yellow: 'from-yellow-50 to-yellow-100 text-yellow-600',
    indigo: 'from-indigo-50 to-indigo-100 text-indigo-600',
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {metrics.map((metric, idx) => (
        <div key={idx} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorMap[metric.color as keyof typeof colorMap]} flex items-center justify-center`}>
              <metric.icon size={24} />
            </div>
            <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <TrendingUp size={16} />
              {metric.changePercent}
            </div>
          </div>
          <div className="text-3xl font-bold text-foreground mb-1">{metric.value}</div>
          <div className="text-sm font-medium text-muted-foreground mb-2">{metric.label}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="text-green-600 font-medium">{metric.change} this month</span>
            <span>•</span>
            <span>{metric.description}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
